import React, { useState } from 'react';
import { email, Notification, required, TextInput } from 'react-admin';
import { Form } from 'react-final-form';
import {
  Button,
  CardActions,
  CircularProgress,
  Typography,
  Card,
  InputAdornment,
  IconButton,
  TextField,
  Checkbox,
  FormControlLabel,
} from '@material-ui/core';
import { ThemeProvider } from '@material-ui/core/styles';
import { VisibilityOffOutlined, VisibilityOutlined } from '@material-ui/icons';

import { theme } from '../../core/theming';
import BrandMark from '../../components/atoms/brand-mark';

import { useStyles } from './styles';
import { useAuth } from './hook';

// Marco común (tarjeta + encabezado + notificación) para todas las fases.
let AuthShell = ({ title, eyebrow, children }: any) => {
  let styles = useStyles();
  return (
    <div className={styles.main}>
      <Card className={styles.card}>
        <div className={styles.headerContainer}>
          <BrandMark wordmark={false} size={52} />
          <Typography className={styles.eyebrow}>
            {eyebrow ?? 'Acceso restringido'}
          </Typography>
          <Typography variant="h5" component="h1" className={styles.title}>
            {title}
          </Typography>
        </div>
        {children}
      </Card>
      <Notification />
    </div>
  );
};

// Fase 1: email + contraseña (o recuperación de contraseña).
let CredentialsPhase = ({ auth }: any) => {
  let styles = useStyles();
  let { loading, resetPassword, setResetPassword, handleSubmit } = auth;
  let [showPassword, setShowPassword] = useState(false);

  return (
    <Form
      onSubmit={handleSubmit}
      render={({ handleSubmit: submit, hasValidationErrors }) => (
        <AuthShell
          title={resetPassword ? 'Recuperar contraseña' : 'Ingresar a la consola'}
        >
          <div className={styles.content}>
            {resetPassword ? (
              <TextInput
                source="email"
                label="Email"
                variant="outlined"
                disabled={loading}
                className={styles.input}
                validate={[required(), email()]}
                fullWidth
              />
            ) : (
              <>
                <TextInput
                  autoFocus
                  source="username"
                  label="Email"
                  variant="outlined"
                  disabled={loading}
                  className={styles.input}
                  validate={[required(), email()]}
                  fullWidth
                />
                <TextInput
                  source="password"
                  label="Contraseña"
                  variant="outlined"
                  disabled={loading}
                  validate={[required()]}
                  type={showPassword ? 'text' : 'password'}
                  fullWidth
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="mostrar u ocultar contraseña"
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? (
                            <VisibilityOutlined />
                          ) : (
                            <VisibilityOffOutlined />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </>
            )}
          </div>
          <CardActions className={styles.actions}>
            <Button
              variant="contained"
              type="submit"
              color="primary"
              disabled={loading || hasValidationErrors}
              fullWidth
              className={styles.submitButton}
              onClick={submit}
            >
              {loading ? (
                <CircularProgress size={22} thickness={4} />
              ) : resetPassword ? (
                'Enviar enlace de recuperación'
              ) : (
                'Ingresar'
              )}
            </Button>
            <Button
              variant="text"
              color="secondary"
              disabled={loading}
              fullWidth
              className={styles.toggleButton}
              onClick={() => setResetPassword(!resetPassword)}
            >
              {resetPassword
                ? 'Volver al inicio de sesión'
                : 'Olvidé mi contraseña'}
            </Button>
          </CardActions>
        </AuthShell>
      )}
    />
  );
};

// Fase 2 (sólo primer enrolamiento): mostrar el QR para escanear.
let EnrollPhase = ({ auth }: any) => {
  let styles = useStyles();
  let { qrDataUrl, goToCodeStep } = auth;

  return (
    <AuthShell
      eyebrow="Verificación en dos pasos"
      title="Configurar Google Authenticator"
    >
      <div className={styles.content}>
        <Typography className={styles.helpText}>
          Escaneá el código con Google Authenticator (o tu app de autenticación).
          Después vas a ingresar el código de 6 dígitos que genera.
        </Typography>
        <div className={styles.qrWrap}>
          {qrDataUrl ? (
            <img src={qrDataUrl} alt="Código QR" className={styles.qrImg} />
          ) : (
            <CircularProgress size={28} thickness={4} />
          )}
        </div>
      </div>
      <CardActions className={styles.actions}>
        <Button
          variant="contained"
          color="primary"
          fullWidth
          className={styles.submitButton}
          disabled={!qrDataUrl}
          onClick={goToCodeStep}
        >
          Continuar
        </Button>
      </CardActions>
    </AuthShell>
  );
};

// Fase 3: ingresar el código de 6 dígitos (o uno de recuperación en re-login).
let CodePhase = ({ auth }: any) => {
  let styles = useStyles();
  let { loading, handleCode, alreadyEnrolled } = auth;
  let [code, setCode] = useState('');

  let submit = () => handleCode(code.trim());

  let onChange = (value: string) => {
    setCode(value);
    // Auto-verifica al completar 6 dígitos (los códigos de recuperación llevan
    // guión, así que no disparan esto: se confirman con el botón/Enter).
    if (/^\d{6}$/.test(value.trim())) handleCode(value.trim());
  };

  return (
    <AuthShell eyebrow="Verificación en dos pasos" title="Ingresá tu código">
      <div className={styles.content}>
        <Typography className={styles.helpText}>
          Ingresá el código de 6 dígitos de tu app.
          {alreadyEnrolled
            ? ' Si perdiste el teléfono, podés usar un código de recuperación.'
            : ''}
        </Typography>
        <TextField
          autoFocus
          variant="outlined"
          fullWidth
          className={styles.input}
          label="Código"
          value={code}
          disabled={loading}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') submit();
          }}
          inputProps={{
            inputMode: 'text',
            autoComplete: 'one-time-code',
            maxLength: 20,
          }}
        />
      </div>
      <CardActions className={styles.actions}>
        <Button
          variant="contained"
          color="primary"
          fullWidth
          className={styles.submitButton}
          disabled={loading || !code.trim()}
          onClick={submit}
        >
          {loading ? (
            <CircularProgress size={22} thickness={4} />
          ) : (
            'Verificar'
          )}
        </Button>
      </CardActions>
    </AuthShell>
  );
};

// Fase 4 (sólo primer enrolamiento): mostrar UNA vez los códigos de recuperación.
let RecoveryPhase = ({ auth }: any) => {
  let styles = useStyles();
  let { recoveryCodes, finishRecovery } = auth;
  let [saved, setSaved] = useState(false);

  return (
    <AuthShell
      eyebrow="Verificación en dos pasos"
      title="Códigos de recuperación"
    >
      <div className={styles.content}>
        <Typography className={styles.helpText}>
          Guardá estos códigos en un lugar seguro. Cada uno sirve una sola vez si
          perdés el teléfono. No se vuelven a mostrar.
        </Typography>
        <div className={styles.codesGrid}>
          {recoveryCodes.map((c: string) => (
            <span key={c} className={styles.codeItem}>
              {c}
            </span>
          ))}
        </div>
        <FormControlLabel
          className={styles.savedCheck}
          control={
            <Checkbox
              checked={saved}
              onChange={(e) => setSaved(e.target.checked)}
              color="primary"
            />
          }
          label="Los guardé en un lugar seguro"
        />
      </div>
      <CardActions className={styles.actions}>
        <Button
          variant="contained"
          color="primary"
          fullWidth
          className={styles.submitButton}
          disabled={!saved}
          onClick={finishRecovery}
        >
          Entrar a la consola
        </Button>
      </CardActions>
    </AuthShell>
  );
};

export default function CustomLogin() {
  let auth = useAuth();
  let { phase } = auth;

  return (
    <ThemeProvider theme={theme}>
      {phase === 'credentials' && <CredentialsPhase auth={auth} />}
      {phase === 'enroll' && <EnrollPhase auth={auth} />}
      {phase === 'code' && <CodePhase auth={auth} />}
      {phase === 'recovery' && <RecoveryPhase auth={auth} />}
    </ThemeProvider>
  );
}
